import type { FeatureServices } from '@life-os/app-core';
import { makeBlock } from '@life-os/app-core';
import type { GraphService } from '@life-os/data';

/**
 * Populate a realistic cross-section of Life OS so the app is explorable on
 * first run. Everything goes through the normal services, so it is real,
 * local-first data the owner can edit, archive, or build on — not fixtures.
 *
 * When a GraphService is supplied, a few representative cross-domain links are
 * created so the relationship graph ("one object, many lenses") is visible.
 */
export async function seedDemoData(services: FeatureServices, graph?: GraphService): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);

  // Direction: goals across pillars + a multi-goal milestone.
  const security = await services.direction.createGoal({
    title: 'Become a security engineer',
    pillar: 'mission',
    horizon: 'year',
    why: 'Meaningful, well-paid work I am proud of.',
  });
  const body = await services.direction.createGoal({
    title: 'Strong, energetic body',
    pillar: 'physical',
    horizon: 'year',
    why: 'Energy is the base layer for everything else.',
  });
  const milestone = await services.direction.createMilestone({
    title: 'Finish networking fundamentals',
    contributesToGoalIds: [security.id],
  });
  await services.direction.setMilestoneStatus(milestone.id, 'in_progress');
  const bodyMilestone = await services.direction.createMilestone({
    title: 'Train consistently for a month',
    contributesToGoalIds: [body.id],
  });
  await services.direction.setMilestoneStatus(bodyMilestone.id, 'in_progress');

  // Targets: a weekly output + a practice target with one logged evidence.
  await services.targets.create({ title: 'Publish one writeup', targetType: 'output_target', cadence: 'weekly', quantity: 1 });
  const ctf = await services.targets.create({ title: 'CTF / lab practice', targetType: 'practice_target', cadence: 'weekly', quantity: 5 });
  await services.targets.logEvidence(ctf.id, 'Completed a packet-capture lab.');

  // Routines & habits.
  const meditate = await services.routines.createHabit({ title: 'Meditate', weekdayMask: [true, true, true, true, true, true, true] });
  await services.routines.createHabit({ title: 'Strength training', weekdayMask: [false, true, false, true, false, true, false] });
  await services.routines.createRoutine({
    title: 'Morning routine',
    dayType: 'everyday',
    steps: [
      { label: 'Hydrate', minutes: 2 },
      { label: 'Stretch', minutes: 8 },
      { label: 'Plan the day', minutes: 5 },
    ],
  });

  // Projects.
  const portfolio = await services.projects.create({
    title: 'Cybersecurity portfolio',
    summary: 'Public proof of practical security skill.',
    definitionOfDone: 'Six published lab writeups.',
  });

  // Problems & repair.
  const perfectionism = await services.problems.create({
    title: 'I avoid publishing until it feels perfect',
    controlCategory: 'control',
    limiterType: 'false',
    cause: 'Fear of judgement; perfectionism.',
    intervention: 'Ship a rough writeup, then improve in public.',
    nextStep: 'Publish one imperfect note this week.',
  });

  // Tasks (Now / Next).
  const labTask = await services.tasks.create({ title: 'Set up home lab VM', lane: 'now' });
  await services.tasks.create({ title: 'Outline first writeup', lane: 'next' });

  // Calendar: a recurring deep-work block today.
  await services.events.create({
    title: 'Deep work block',
    startsAt: `${today}T09:00:00.000Z`,
    durationMinutes: 90,
    recurrence: { freq: 'daily', interval: 1, until: null },
  });
  await services.events.create({
    title: 'Evening review',
    startsAt: `${today}T20:00:00.000Z`,
    durationMinutes: 20,
    recurrence: { freq: 'daily', interval: 1, until: null },
  });

  // Notes + a wiki link so the knowledge graph has an edge.
  await services.notes.create({
    title: 'Networking',
    blocks: [makeBlock('heading', 'Networking'), makeBlock('paragraph', 'Core of most security work.')],
    tags: ['security', 'foundations'],
  });
  await services.notes.create({
    title: 'TCP/IP',
    blocks: [makeBlock('paragraph', 'A key part of [[Networking]]. Study the layers and handshakes.')],
    tags: ['security'],
  });

  // Health: a calm daily reading.
  await services.health.log({ sleepHours: 7.5, energy: 4, movementMinutes: 30, note: 'Felt good after a walk.' });

  // Finance: an account with one income + one expense.
  const checking = await services.finance.createAccount({ name: 'Checking', accountType: 'bank', openingBalanceMinor: 250_000 });
  await services.finance.recordTransaction({ accountId: checking.id, txnKind: 'income', amountMinor: 300_000, category: 'salary' });
  await services.finance.recordTransaction({ accountId: checking.id, txnKind: 'expense', amountMinor: 4_500, category: 'groceries' });

  // A couple of unreviewed captures to exercise the daily loop.
  await services.capture.captureItem({ rawText: 'Idea: write about DNS exfiltration detection' });
  await services.capture.captureItem({ rawText: 'Ask mentor about cloud security certs' });

  // Study: a subject mid-progress with a logged session.
  const networking = await services.study.createSubject({ title: 'Networking', masteryState: 'practicing' });
  await services.study.logSession({ subjectId: networking.id, minutes: 45, note: 'TCP/IP handshake lab.' });

  // Career: a couple of opportunities in the pipeline.
  const role = await services.career.create({ title: 'Junior Security Engineer', organization: 'Acme' });
  await services.career.setStage(role.id, 'applied');
  await services.career.create({ title: 'SOC Analyst', organization: 'Globex' });

  // Mental & Spiritual journals.
  await services.journal.add({ stream: 'mental', entryKind: 'reflection', body: 'Shipped something rough today. The world did not end.' });
  await services.journal.add({ stream: 'spiritual', entryKind: 'gratitude', body: 'Grateful for a quiet morning and a clear plan.' });

  // Work & Content: a piece in draft.
  const writeup = await services.content.create({ title: 'How DNS exfiltration works', platform: 'blog' });
  await services.content.setStage(writeup.id, 'draft');

  // Business: a small venture with revenue.
  const venture = await services.business.create({ title: 'Shadownik', sector: 'consulting' });
  await services.business.setStatus(venture.id, 'active');
  await services.business.update(venture.id, { monthlyRevenueMinor: 120_000 });

  // Environment: a device and a tool.
  await services.environment.create({ name: 'Laptop', category: 'device', location: 'Desk' });
  await services.environment.create({ name: 'Home lab server', category: 'device', location: 'Closet' });

  // A reminder due later today.
  const at = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString();
  await services.reminders.schedule({ label: 'Review the day and plan tomorrow', fireAt: at });

  // A focus session already logged today.
  await services.focus.logSession({ minutes: 25, note: 'Morning deep work block.' });

  // Cross-domain relationships: one object, many lenses. These connect the
  // canonical entities above so backlinks and "used in" rails have real data.
  if (graph) {
    const goalRef = { kind: 'goal' as const, id: security.id };
    const bodyRef = { kind: 'goal' as const, id: body.id };
    await graph.link({ kind: 'milestone', id: milestone.id }, goalRef, 'part_of');
    await graph.link({ kind: 'project', id: portfolio.id }, goalRef, 'supports');
    await graph.link({ kind: 'target', id: ctf.id }, goalRef, 'supports');
    await graph.link({ kind: 'problem', id: perfectionism.id }, goalRef, 'blocks');
    await graph.link({ kind: 'task', id: labTask.id }, { kind: 'project', id: portfolio.id }, 'part_of');
    await graph.link({ kind: 'habit', id: meditate.id }, bodyRef, 'supports');
  }
}


/**
 * Load the owner's REAL data — profile, education, the Edinburgh MSc admission,
 * its funding sources, the critical deposit action + reminder, and the proactive
 * Bhopal trip. Real, editable, local-first records (not demo fixtures). Intended
 * to run once on a fresh store.
 */
export async function seedOwnerData(services: FeatureServices, graph?: GraphService): Promise<void> {
  const gbp = (pounds: number): number => Math.round(pounds * 100); // -> minor units (pence)

  await services.academics.saveProfile({
    displayName: 'Nikhil Nagpure',
    headline: 'Cybersecurity & CS engineer · MSc-bound (Edinburgh) · builder',
    location: 'India',
    education: [
      {
        institution: 'Vellore Institute of Technology (VIT)',
        qualification: 'B.Tech',
        field: 'Cyber Security & Computer Science Engineering',
        status: 'completed',
        endYear: 2025,
        note: 'Undergraduate degree.',
      },
      {
        institution: 'University of Edinburgh',
        qualification: 'MSc',
        field: 'Postgraduate study',
        status: 'planned',
        endYear: null,
        note: 'Conditional offer — September 2026 intake.',
      },
    ],
  });

  const edinburghGoal = await services.direction.createGoal({
    title: 'Fund and enrol at the University of Edinburgh (MSc, Sept 2026)',
    pillar: 'intellectual',
    horizon: 'year',
    why: 'A better growing environment in the UK — a launchpad for the bigger mission.',
  });

  const admission = await services.academics.createAdmission({
    institution: 'University of Edinburgh',
    programme: "Master's (MSc) — September 2026 intake",
    offerStatus: 'conditional',
    conditions: ['Pay £1,500 deposit by 30 June 2026 (extension granted) to secure the place'],
    currency: 'GBP',
    tuitionMinor: gbp(45100),
    depositMinor: gbp(1500),
    livingEstimateMinor: gbp(18504),
    depositDueDate: '2026-06-30',
    intakeTerm: 'September 2026',
    note: 'Tuition ~£45,100. Living ~£18,504/yr (Edinburgh estimate). Visa maintenance min ~£10,539.',
  });

  const scholarship = await services.academics.addFunding({
    name: 'Fully-funded scholarship (primary hope)',
    sourceKind: 'scholarship',
    status: 'planned',
    currency: 'GBP',
    amountMinor: gbp(63600),
    fullyFunded: true,
    nextAction: 'Confirm exact application open date with the scholarship office',
    nextActionDate: '2026-06-20',
    note: 'Applications not open yet — expected soon. Visited the head office in person for updates.',
  });
  // Real proactive pursuit already done — logged as the first touchpoint.
  await services.academics.logTouchpoint(scholarship.id, {
    on: new Date().toISOString().slice(0, 10),
    channel: 'visit',
    note: 'Travelled to Bhopal and visited the scholarship head office in person for updates.',
  });
  await services.academics.addFunding({
    name: 'Backup: education loan',
    sourceKind: 'loan',
    status: 'planned',
    currency: 'GBP',
    note: 'De-risk in parallel so the seat is never one "no" away from being lost.',
  });
  await services.academics.addFunding({
    name: 'Backup: family + savings (deposit cover)',
    sourceKind: 'savings',
    status: 'planned',
    currency: 'GBP',
    amountMinor: gbp(1500),
    note: 'Cover the £1,500 deposit by 30 June regardless of the scholarship timeline.',
  });

  const depositTask = await services.tasks.create({
    title: 'Pay £1,500 Edinburgh deposit (secures the conditional offer)',
    lane: 'next',
  });
  await services.reminders.schedule({
    label: 'Edinburgh deposit due 30 June — pay the £1,500 to lock the offer',
    fireAt: '2026-06-28T09:00:00.000Z',
  });
  const scholarshipTask = await services.tasks.create({
    title: 'Apply to the fully-funded scholarship the day applications open',
    lane: 'next',
  });

  const todayIso = new Date().toISOString().slice(0, 10);
  await services.events.create({
    title: 'Bus to Bhopal — visit scholarship head office for updates',
    startsAt: `${todayIso}T18:35:00.000Z`,
    durationMinutes: 8 * 60,
    location: 'Bhopal (overnight bus)',
  });

  if (graph) {
    const goalRef = { kind: 'goal' as const, id: edinburghGoal.id };
    const admissionRef = { kind: 'admission' as const, id: admission.id };
    await graph.link(admissionRef, goalRef, 'part_of');
    await graph.link({ kind: 'funding_source', id: scholarship.id }, admissionRef, 'supports');
    await graph.link({ kind: 'task', id: depositTask.id }, admissionRef, 'part_of');
    await graph.link({ kind: 'task', id: scholarshipTask.id }, goalRef, 'supports');
  }
}
